import {chain, groupBy, sortBy} from 'lodash';
import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {AnswerModel} from '../../../models/api/answerModel';
import {TeamModel} from '../../../models/api/teamModel';
import {AnswersSortOrdersEnum} from '../../../models/api/teamPreferencesModel';
import {AppState} from '../../appState';
import {AsyncStatus} from '../../appTypes';
import {getHasLegacyMessageTemplatesSorting} from '../../featureFlags/featureFlagsSelectors';
import {getUser, getUserNamespace} from '../teammates/teammatesSelectors';
import {getFlatTeams} from '../teams/teamsSelectors';

/** Get a map of all answers. */
export function getAnswers(state: AppState) {
  return state.data.answers.byId;
}

export function getAnswersAsyncStatus(state: AppState): AsyncStatus {
  return state.data.answers;
}

/** Get a flat list of all answers. */
export const getFlatAnswers = createSelector(getAnswers, (answers) =>
  chain(answers).values().compact().value(),
);

/** Get a map of answers by namespaces */
export const getAnswersByNamespaces = createSelector(
  [getFlatAnswers],
  (answers): Record<string, ReadonlyArray<AnswerModel> | undefined> =>
    groupBy(answers, (answer) => answer.namespace),
);

type NamespaceTeams = {[T: string]: TeamModel | undefined};
export const getNamespaceTeams = createSelector([getFlatTeams], (teams) =>
  // Build a map of namespace teams.
  teams.reduce((result, team) => {
    result[team.namespace] = team; // eslint-disable-line no-param-reassign
    return result;
  }, {} as NamespaceTeams),
);

type NamespaceIndexes = {[T: string]: number | undefined};
export const getNamespaceIndexes = createSelector([getFlatTeams], (teams) =>
  // Build a map of namespace indexes.
  teams.reduce((result, team, index) => {
    result[team.namespace] = index; // eslint-disable-line no-param-reassign
    return result;
  }, {} as NamespaceIndexes),
);

/** Get a sorted flat list of all answers. */
export const getSortedAnswers = createSelector(
  [getUser, getFlatAnswers, getNamespaceTeams, getNamespaceIndexes, getHasLegacyMessageTemplatesSorting],
  (user, answers, namespaceTeams, namespaceIndexes, hasLegacyMessageTemplatesSorting) =>
    // Sort the answers by personal/share, team, team order.
    sortBy(
      answers,
      (a) => a.namespace !== user.namespace,
      (a) => namespaceIndexes[a.namespace],
      (a) => getAnswerSortOrder(a, hasLegacyMessageTemplatesSorting, namespaceTeams[a.namespace]),
    ),
);

function getAnswerSortOrder(
  answer: AnswerModel,
  hasLegacyMessageTemplatesSorting: boolean,
  team?: TeamModel,
) {
  // Sort alphanumerically when no team was found (i.e, private answers).
  if (!team || !hasLegacyMessageTemplatesSorting) {
    return answer.name;
  }

  // Return the corresponding property from the answer.
  switch (team.preferences.answersSortOrder) {
    case AnswersSortOrdersEnum.DATE_CREATED:
      return answer.createdAt;

    case AnswersSortOrdersEnum.CUSTOM:
      return answer.sortOrder;

    default:
      return answer.name;
  }
}

export function makeGetAnswer<P>(getAnswerId: AppParametricSelector<P, number | undefined>) {
  return createSelector([getAnswers, getAnswerId], (answers, id) => (id ? answers[id] : undefined));
}

export const getUserOwnedAnswers = createSelector(
  [getUserNamespace, getAnswersByNamespaces],
  (userNamespace, answersByNamespace) => answersByNamespace[userNamespace] || [],
);

export const getUserOwnedAnswersCount = createSelector(
  getUserOwnedAnswers,
  (userOwnedAnswers) => userOwnedAnswers?.length || 0,
);
